import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, User, CreditCard, History, Settings, LogOut, Calendar, Crown, HelpCircle, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ConnectWithUsModal from '../common/ConnectWithUsModal';
import ReferralModal from '../settings/ReferralModal';
import ReferralStatsModal from '../settings/ReferralStatsModal';
import AmbassadorPaymentsModal from '../settings/AmbassadorPaymentsModal';
import referralService, { AmbassadorStatus, AmbassadorEarnings, PayoutInfo } from '../../services/referralService';
import { useToast } from '../common/Toast';
import type { ReferredUser } from '../../services/referralService';

interface UserProfileMenuProps {
  user: {
    name: string;
    profilePicture?: string;
    profileImage?: string;
    subscriptionPlan?: string;
    isRecurring?: boolean;
    subscriptionExpiry?: string;
    remainingMinutes?: number;
    isActive?: boolean;
  };
  isMobile?: boolean;
  onLogoutClick?: () => void;
  isInMobileMenu?: boolean;
}

export const UserProfileMenu: React.FC<UserProfileMenuProps> = React.memo(({ 
  user, 
  isMobile = false,
  onLogoutClick,
  isInMobileMenu = false
}) => {
  // Get the profile image from either profilePicture or profileImage property
  const profileImage = user.profilePicture || user.profileImage || '';
  // Determine if user has a paid plan (not free)
  const hasPaidPlan = user.subscriptionPlan && !user.subscriptionPlan.toLowerCase().includes('free');
  

  const [isOpen, setIsOpen] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);

  // Referral & Ambassador state/logic
  const { showToast } = useToast();
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [isLoadingReferral, setIsLoadingReferral] = useState(false);
  const [referralStats, setReferralStats] = useState({ totalReferrals: 0, activeSignups: 0, subscribed: 0, minutesEarned: 0 });
  const [referredUsers, setReferredUsers] = useState<ReferredUser[]>([]);
  const [ambassadorStatus, setAmbassadorStatus] = useState<AmbassadorStatus>({ isAmbassador: false, status: null, application: null });
  const [ambassadorEarnings, setAmbassadorEarnings] = useState<AmbassadorEarnings>({ totalEarnings: 0, pendingEarnings: 0, totalPayouts: 0, totalReferrals: 0, successfulReferrals: 0 });
  const [ambassadorPayouts, setAmbassadorPayouts] = useState<PayoutInfo[]>([]);
  const [showReferralStatsModal, setShowReferralStatsModal] = useState(false);
  const [showAmbassadorPaymentsModal, setShowAmbassadorPaymentsModal] = useState(false);

  const fetchReferralInfo = useCallback(async () => {
    try {
      setIsLoadingReferral(true);
      const code = await referralService.getMyReferralCode();
      setReferralCode(code || '');
      setReferralLink(code ? `${window.location.origin}/signup?ref=${code}` : '');
      const stats = await referralService.getReferralStatistics();
      setReferralStats({
        totalReferrals: stats.totalReferrals,
        activeSignups: stats.activeSignups,
        subscribed: stats.subscribed,
        minutesEarned: stats.minutesEarned
      });
      const history = await referralService.getReferralHistory({ limit: 6, page: 1 });
      setReferredUsers(history.data);
    } catch (error) {
      showToast('Failed to load referral data. Please try refreshing.', 'error');
    } finally {
      setIsLoadingReferral(false);
    }
  }, [showToast]);

  const fetchAmbassadorInfo = useCallback(async () => {
    try {
      setIsLoadingReferral(true);
      const status = await referralService.getAmbassadorStatus();
      setAmbassadorStatus(status);
      if (status.isAmbassador && status.status === 'approved') {
        const [earnings, payouts] = await Promise.all([
          referralService.getAmbassadorEarnings(),
          referralService.getAmbassadorPayouts()
        ]);
        setAmbassadorEarnings(earnings);
        setAmbassadorPayouts(payouts.payouts);
      }
    } catch (error) {
      // Optional: showToast('Failed to load ambassador data.', 'error');
    } finally {
      setIsLoadingReferral(false);
    }
  }, []);

  useEffect(() => {
    if (showReferralModal) {
      fetchReferralInfo();
      fetchAmbassadorInfo();
    }
  }, [showReferralModal, fetchReferralInfo, fetchAmbassadorInfo]);

  const handleCopyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    setLinkCopied(true);
    showToast('Referral link copied to clipboard!', 'success');
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const handleOpenReferralStats = async () => {
    try {
      const history = await referralService.getReferralHistory({ limit: 10, page: 1 });
      setReferredUsers(history.data);
      setShowReferralStatsModal(true);
    } catch (error) {
      showToast('Failed to fetch latest referral data. Please try again.', 'error');
    }
  };

  const handleOpenAmbassadorPayments = async () => {
    try {
      setIsLoadingReferral(true);
      const [earnings, payouts] = await Promise.all([
        referralService.getAmbassadorEarnings(),
        referralService.getAmbassadorPayouts()
      ]);
      setAmbassadorEarnings(earnings);
      setAmbassadorPayouts(payouts.payouts);
      setShowAmbassadorPaymentsModal(true);
    } catch (error) {
      showToast('Failed to fetch latest payment data. Please try again.', 'error');
    } finally {
      setIsLoadingReferral(false);
    }
  };

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // Add a small delay before enabling the click outside handler
    // This prevents the menu from immediately closing when it's just opened
    let timeoutId: NodeJS.Timeout;
    if (isOpen) {
      timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
    }

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle logout with confirmation
  const handleLogout = () => {
    // Call the logout function first, to ensure tokens are used for the API call
    if (onLogoutClick) {
      console.log("UserProfileMenu: Initiating logout");
      onLogoutClick();
      
      // Close menu after a tiny delay to ensure the logout call is processed
      // with tokens still available
      setTimeout(() => {
        setIsOpen(false);
      }, 100);
    } else {
      // If no logout handler, just close the menu
      setIsOpen(false);
    }
  };

  // Add formatDate function
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // Menu items with their icons and actions
  const menuItems = [
    {
      label: 'My Profile (JobProt)',
      icon: <User size={16} />,
      action: () => {
        window.open('https://dashboard.jobprot.com/seeker/dashboard', '_blank');
        setIsOpen(false);
      }
    },
    // Show My Interviews only on mobile
    ...(isMobile ? [{
      label: 'My Interviews',
      icon: <History size={16} />,
      action: () => {
        navigate('/my-interviews');
        setIsOpen(false);
      }
    }] : []),
    {
      label: 'Manage Subscription',
      icon: <CreditCard size={16} />,
      action: () => {
        navigate('/subscriptions');
        setIsOpen(false);
      }
    },
    {
      label: 'Refer & Earn',
      icon: <Gift size={16} className="text-secondary-light dark:text-secondary-dark" />,
      action: () => {
        setShowReferralModal(true);
        setIsOpen(false);
      },
      isGreen: true
    },
    {
      label: 'Settings & Preferences',
      icon: <Settings size={16} />,
      action: () => {
        navigate('/settings-preferences');
        setIsOpen(false);
      }
    },
    {
      label: 'Help & Support',
      icon: <HelpCircle size={16} />,
      action: () => {
        console.log('Help & Support clicked, setting showConnectModal to true');
        setShowConnectModal(true);
        setIsOpen(false);
      }
    }
  ];

  // For mobile menu items and logout button
  const renderMenuItems = () => (
    <>
      {menuItems.map((item, index) => (
        <button
          key={index}
          onClick={item.action}
          className={`
            flex items-center ${(item.label === 'Help & Support') ? 'w-auto min-w-[fit-content]' : 'w-full'} px-3 py-2.5 rounded-lg 
            hover:bg-background-light/50 dark:hover:bg-background-dark/50 
            transition-all duration-200 ease-out
            translate-y-0 opacity-100 text-sm
          `}
          style={{ transitionDelay: `${120 + index * 50}ms` }}
        >
          <span className="mr-3 text-primary-light dark:text-primary-dark">{item.icon}</span>
          <span className="text-base text-text-primary-light dark:text-text-primary-dark">
            {item.label}
          </span>
        </button>
      ))}
      
      {onLogoutClick && (
        <button
          onClick={handleLogout}
          className={`
            flex items-center w-full px-3 py-2.5 rounded-lg 
            hover:bg-red-50 dark:hover:bg-red-900/10 
            transition-all duration-200 ease-out
            translate-y-0 opacity-100 mt-1 text-sm
          `}
          style={{ transitionDelay: `${120 + menuItems.length * 50}ms` }}
        >
          <span className="mr-3 text-red-500"><LogOut size={16} /></span>
          <span className="text-red-500">Logout</span>
        </button>
      )}
    </>
  );

  // For the mobile menu expanded view with all options visible
  if (isInMobileMenu) {
    return (
      <div className="w-full">
        <div className="flex items-center mb-5">
          <div className="relative">
            {profileImage && !imageError ? (
              <img 
                src={profileImage} 
                onError={() => setImageError(true)}
                className="w-14 h-14 rounded-full mr-4 object-cover border border-border-light dark:border-border-dark"
              />
            ) : (
              <div className="w-14 h-14 rounded-full mr-4 bg-secondary-light dark:bg-secondary-dark/70 flex items-center justify-center">
                {/* <User size={24} className="text-primary-light dark:text-primary-dark" /> */}
                <p className='text-white text-xl font-medium font-heading'>{user.name.charAt(0).toUpperCase()}</p>
              </div>
            )}
          </div>
          
          <div>
            <p className="font-heading font-medium text-text-primary-light dark:text-text-primary-dark text-lg">{user.name.charAt(0).toUpperCase() + user.name.slice(1)}</p>
            {user.subscriptionPlan && (
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                {user.subscriptionPlan}
                {user.subscriptionExpiry && ` • ${user.isRecurring && user.isActive ? 'Renews' : hasPaidPlan ? 'Expires' : 'No Plan Expiry'} ${hasPaidPlan ? user.subscriptionExpiry : ''}`}
              </p>
            )}
          </div>
        </div>
        
        <div className="space-y-1">
          {renderMenuItems()}
        </div>
        
        {/* Connect With Us Modal */}
        <ConnectWithUsModal 
          isOpen={showConnectModal}
          onClose={() => setShowConnectModal(false)}
        />
      </div>
    );
  }

  // Mobile avatar with dropdown icon variant (shown in navbar)
  if (isMobile) {
    return (
      <>
        <div ref={menuRef} className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center rounded-full py-1.5 px-2 bg-surface-light/50 dark:bg-surface-dark/50 hover:bg-surface-light/80 dark:hover:bg-surface-dark/80 border border-border-light/50 dark:border-border-dark/15 hover:border-primary-light/40 dark:hover:border-primary-dark/40 transition-all duration-200 touch-manipulation active:scale-95"
            aria-label="User menu"
          >
            {profileImage && !imageError ? (
              <img 
                src={profileImage} 
                onError={() => setImageError(true)}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-secondary-light dark:bg-secondary-dark/70">
                {/* <User size={16} className="text-primary-light dark:text-primary-dark" /> */}
                <p className='text-white text-lg font-medium font-heading'>{user.name.charAt(0).toUpperCase()}</p>
              </div>
            )}
            <ChevronDown 
              size={16} 
              className={`ml-1 text-text-primary-light dark:text-text-primary-dark transition-transform duration-300 ease-in-out ${isOpen ? 'transform rotate-180' : ''}`}
            />
          </button>

          {/* Mobile Dropdown Menu - Matching Landing Page Menu */}
          <div 
            className={`
              fixed left-4 right-4 rounded-xl shadow-lg 
              border border-border-light dark:border-border-dark
              z-[1000] overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
              ${isOpen ? 'max-h-[600px] opacity-100 transform translate-y-0' : 'max-h-0 opacity-0 pointer-events-none transform -translate-y-4'}
            `}
            style={{ top: 'var(--navbar-height, 56px)' }}
          >
            {/* User info header */}
            <div className={`px-5 py-4 border-b border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark
              transition-all duration-300 ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-4'}`}
              style={{ transitionDelay: isOpen ? '50ms' : '0ms' }}
            >
              <div className="flex items-center">
                {profileImage && !imageError ? (
                  <img 
                    src={profileImage} 
                    onError={() => setImageError(true)}
                    className={`w-14 h-14 rounded-full mr-3 object-cover 
                      transition-all duration-300 ${isOpen ? 'opacity-100 transform scale-100' : 'opacity-0 transform scale-95'}`}
                    style={{ transitionDelay: isOpen ? '100ms' : '0ms' }}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full mr-4 bg-secondary-light dark:bg-secondary-dark/70 flex items-center justify-center">
                    {/* <User size={24} className="text-primary-light dark:text-primary-dark" /> */}
                    <p className='text-white text-3xl font-medium font-heading'>{user.name.charAt(0).toUpperCase()}</p>
                  </div>
                )}
                
                <div className="flex-1">
                  <p className={`font-heading font-medium text-text-primary-light dark:text-text-primary-dark text-lg
                    transition-all duration-300 ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-2'}`}
                    style={{ transitionDelay: isOpen ? '150ms' : '0ms' }}
                  >
                    {user.name.charAt(0).toUpperCase() + user.name.slice(1)}
                  </p>
                  
                  {user.subscriptionPlan && (
                    <div className={`mt-1 flex flex-wrap flex-col items-start gap-2
                      transition-all duration-300 ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-2'}`}
                      style={{ transitionDelay: isOpen ? '200ms' : '0ms' }}
                    >
                      <div className="flex items-center gap-1 bg-primary-light/10 dark:bg-primary-dark/15 py-1 px-2 rounded-full">
                        <Crown size={14} className={hasPaidPlan ? "text-green-500 dark:text-green-400" : "text-amber-500"} />
                        <span className={`text-sm font-medium ${hasPaidPlan ? "text-green-500 dark:text-green-400" : "text-amber-500"}`}>
                          {user.subscriptionPlan.toLowerCase() === 'per_interview' ? 'Per Interview' : user.subscriptionPlan.charAt(0).toUpperCase() + user.subscriptionPlan.slice(1)}
                        </span>
                      </div>
                      {user.subscriptionExpiry && user.subscriptionPlan && !user.subscriptionPlan.toLowerCase().includes('free') && (
                        <div className="flex items-center text-sm text-amber-500">
                          <Calendar size={14} className="mr-1" />
                          <span>{user.isRecurring && user.isActive ? 'Renews' : hasPaidPlan ? 'Expires' : 'No Plan Expiry'} {hasPaidPlan ? user.subscriptionExpiry : ''}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="py-2 bg-surface-light dark:bg-surface-dark">
              {menuItems.map((item, index) => (
                <button
                  key={index}
                  onClick={item.action}
                  className={`
                    flex items-center w-full px-5 py-3
                    hover:bg-background-light dark:hover:bg-background-dark 
                    transition-all duration-1 ease-out text-lg
                    ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-4'}
                  `}
                  style={{ transitionDelay: isOpen ? `${250 + index * 50}ms` : '0ms' }}
                >
                  <span className="mr-4 text-primary-light dark:text-primary-dark">{item.icon}</span>
                  <span className="text-base text-text-primary-light dark:text-text-primary-dark font-medium">
                    {item.label}
                  </span>
                </button>
              ))}
              
              {/* Logout button with different styling */}
              {onLogoutClick && (
                <>
                  <div className={`my-1 border-t border-border-light dark:border-border-dark
                    transition-all duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transitionDelay: isOpen ? `${250 + menuItems.length * 50}ms` : '0ms' }}
                  ></div>
                  <button
                    onClick={handleLogout}
                    className={`
                      flex items-center w-full px-5 py-3.5 text-red-500 
                      hover:bg-red-50 dark:hover:bg-red-900/20 
                      transition-all duration-1 ease-out text-lg
                      ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-4'}
                    `}
                    style={{ transitionDelay: isOpen ? `${300 + menuItems.length * 50}ms` : '0ms' }}
                  >
                    <span className="mr-4"><LogOut size={16} /></span>
                    <span className="text-base font-medium">Logout</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Connect With Us Modal */}
        <ConnectWithUsModal 
          isOpen={showConnectModal}
          onClose={() => setShowConnectModal(false)}
        />
        <ReferralModal
        isOpen={showReferralModal}
        onClose={() => {
          setShowReferralModal(false);
          setShowReferralStatsModal(false);
          setShowAmbassadorPaymentsModal(false);
        }}
        referralCode={referralCode}
        referralLink={referralLink}
        linkCopied={linkCopied}
        isLoadingReferral={isLoadingReferral}
        referralStats={referralStats}
        ambassadorStatus={ambassadorStatus}
        ambassadorEarnings={ambassadorEarnings}
        ambassadorPayouts={ambassadorPayouts}
        showReferralStatsModal={showReferralStatsModal}
        showAmbassadorPaymentsModal={showAmbassadorPaymentsModal}
        referredUsers={referredUsers}
        onCopyReferralLink={handleCopyReferralLink}
        onOpenReferralStats={handleOpenReferralStats}
        onOpenAmbassadorPayments={handleOpenAmbassadorPayments}
        onRefresh={async () => { await fetchReferralInfo(); await fetchAmbassadorInfo(); }}
        onCloseReferralStats={() => setShowReferralStatsModal(false)}
        onCloseAmbassadorPayments={() => setShowAmbassadorPaymentsModal(false)}
        formatDate={formatDate}
        isMobileView={isMobile}
        zIndex={9999}
      />
      <ReferralStatsModal
        isOpen={showReferralStatsModal}
        onClose={() => setShowReferralStatsModal(false)}
        stats={{
          totalReferrals: referralStats.totalReferrals,
          subscribed: referralStats.subscribed,
          minutesEarned: referralStats.minutesEarned
        }}
        referredUsers={referredUsers}
        formatDate={formatDate}
        zIndex={10000}
      />
      <AmbassadorPaymentsModal
        isOpen={showAmbassadorPaymentsModal}
        onClose={() => setShowAmbassadorPaymentsModal(false)}
        earnings={ambassadorEarnings}
        payouts={ambassadorPayouts}
        zIndex={10000}
      />
      </>
    );
  }

  // Desktop variant with dropdown
  return (
    <>
      <div ref={menuRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center px-2 py-1.5 rounded-full bg-surface-light/50 dark:bg-surface-dark/50 hover:bg-surface-light/80 dark:hover:bg-surface-dark/80 border border-border-light/50 dark:border-border-dark/15 hover:border-primary-light/40 dark:hover:border-primary-dark/40 transition-all duration-150 hover:shadow-sm"
        >
          {profileImage && !imageError ? (
            <img 
              src={profileImage} 
              onError={() => setImageError(true)}
              className="w-8 h-8 rounded-full mr-2 object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full mr-2 flex items-center justify-center bg-secondary-light dark:bg-secondary-dark/70">
             <p className='text-white text-lg font-medium font-heading'>{user.name.charAt(0).toUpperCase()}</p>
            </div>
          )}

          {!isMobile && (
          <span className="mr-1 text-sm font-medium truncate max-w-[100px]">{user.name.charAt(0).toUpperCase() + user.name.slice(1)}</span>
          )}
          <ChevronDown 
            size={16} 
            className={`transition-transform duration-300 ease-in-out ${isOpen ? 'transform rotate-180' : ''}`}
          />
        </button>

        {/* Desktop Dropdown Menu */}
        <div 
          className={`
            fixed md:absolute right-0 left-4 md:left-auto top-[var(--navbar-height,56px)] md:top-full w-[calc(100%-2rem)] md:w-64 rounded-xl shadow-lg 
            border border-border-light dark:border-border-dark
            z-[1000] overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]
            md:mt-2 origin-top-right
            ${isOpen ? 'transform scale-100 opacity-100' : 'transform scale-95 opacity-0 pointer-events-none'}
          `}
        >
          {/* User info header */}
          <div className={`px-4 py-3 border-b border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark
            transition-all duration-300 ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-2'}`}
            style={{ transitionDelay: isOpen ? '50ms' : '0ms' }}
          >
            <div className="flex items-center">
              {profileImage && !imageError ? (
                <img 
                  src={profileImage} 
                  onError={() => setImageError(true)}
                  className={`w-12 h-12 rounded-full mr-3 object-cover 
                    transition-all duration-300 ${isOpen ? 'opacity-100 transform scale-100' : 'opacity-0 transform scale-95'}`}
                  style={{ transitionDelay: isOpen ? '100ms' : '0ms' }}
                />
              ) : (
                <div className={`w-12 h-12 rounded-full mr-3 flex items-center justify-center bg-secondary-light dark:bg-secondary-dark/70
                    transition-all duration-300 ${isOpen ? 'opacity-100 transform scale-100' : 'opacity-0 transform scale-95'}`}
                    style={{ transitionDelay: isOpen ? '100ms' : '0ms' }}
                >
                  {/* <User size={20} className="text-primary-light dark:text-primary-dark" /> */}
                  <p className='text-white text-3xl font-medium font-heading'>{user.name.charAt(0).toUpperCase()}</p>
                </div>
              )}
              
              <div className="flex-1">
                <p className={`font-heading font-medium text-text-primary-light dark:text-text-primary-dark text-sm
                  transition-all duration-300 ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-2'}`}
                  style={{ transitionDelay: isOpen ? '150ms' : '0ms' }}
                >
                  {user.name.charAt(0).toUpperCase() + user.name.slice(1)}
                </p>
                
                {user.subscriptionPlan && (
                  <div className={`mt-1 flex items-center flex-wrap gap-1
                    transition-all duration-300 ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-2'}`}
                    style={{ transitionDelay: isOpen ? '200ms' : '0ms' }}
                  >
                    <div className="flex items-center gap-1 bg-primary-light/10 dark:bg-primary-dark/15 py-0.5 px-1.5 rounded-full">
                      <Crown size={10} className={hasPaidPlan ? "text-green-500 dark:text-green-400" : "text-amber-500"} />
                      <span className={`text-xs font-medium ${hasPaidPlan ? "text-green-500 dark:text-green-400" : "text-amber-500"}`}>
                      {user.subscriptionPlan.toLowerCase() === 'per_interview' ? 'Per Interview' : user.subscriptionPlan.charAt(0).toUpperCase() + user.subscriptionPlan.slice(1)}
                      </span>
                    </div>
                    {user.subscriptionExpiry && user.subscriptionPlan && !user.subscriptionPlan.toLowerCase().includes('free') && (
                      <div className="flex items-center text-xs text-amber-500">
                        <Calendar size={12} className="mr-1" />
                        <span>{user.isRecurring && user.isActive ? 'Renews' : hasPaidPlan ? 'Expires' : 'No Plan Expiry'} {hasPaidPlan ? user.subscriptionExpiry : ''}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-1.5 bg-surface-light dark:bg-surface-dark">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.action}
                className={`
                  font-medium
                  flex items-center w-full px-4 py-2 
                  hover:bg-background-light dark:hover:bg-background-dark 
                  transition-all duration-1 ease-out text-base md:text-sm
                  ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-2'}
                `}
                style={{ transitionDelay: isOpen ? `${250 + index * 50}ms` : '0ms' }}
              >
                <span className="mr-3 text-primary-light dark:text-primary-dark">{item.icon}</span>
                <span className="text-sm text-text-primary-light dark:text-text-primary-dark">
                  {item.label}
                </span>
              </button>
            ))}
            
            {/* Logout button with different styling */}
            {onLogoutClick && (
              <>
                <div className={`my-1 border-t border-border-light dark:border-border-dark
                  transition-all duration-200 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
                  style={{ transitionDelay: isOpen ? `${250 + menuItems.length * 50}ms` : '0ms' }}
                ></div>
                <button
                  onClick={handleLogout}
                  className={`
                    flex items-center w-full px-4 py-2 text-red-500 
                    hover:bg-red-50 dark:hover:bg-red-900/20 
                    transition-all duration-1 ease-out text-sm
                    ${isOpen ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform -translate-y-2'}
                  `}
                  style={{ transitionDelay: isOpen ? `${300 + menuItems.length * 50}ms` : '0ms' }}
                >
                  <span className="mr-3"><LogOut size={16} /></span>
                  <span className="font-medium">Logout</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Connect With Us Modal */}
      <ConnectWithUsModal 
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
      />
      <ReferralModal
        isOpen={showReferralModal}
        onClose={() => {
          setShowReferralModal(false);
          setShowReferralStatsModal(false);
          setShowAmbassadorPaymentsModal(false);
        }}
        referralCode={referralCode}
        referralLink={referralLink}
        linkCopied={linkCopied}
        isLoadingReferral={isLoadingReferral}
        referralStats={referralStats}
        ambassadorStatus={ambassadorStatus}
        ambassadorEarnings={ambassadorEarnings}
        ambassadorPayouts={ambassadorPayouts}
        showReferralStatsModal={showReferralStatsModal}
        showAmbassadorPaymentsModal={showAmbassadorPaymentsModal}
        referredUsers={referredUsers}
        onCopyReferralLink={handleCopyReferralLink}
        onOpenReferralStats={handleOpenReferralStats}
        onOpenAmbassadorPayments={handleOpenAmbassadorPayments}
        onRefresh={async () => { await fetchReferralInfo(); await fetchAmbassadorInfo(); }}
        onCloseReferralStats={() => setShowReferralStatsModal(false)}
        onCloseAmbassadorPayments={() => setShowAmbassadorPaymentsModal(false)}
        formatDate={formatDate}
        isMobileView={isMobile}
        zIndex={9999}
      />
      <ReferralStatsModal
        isOpen={showReferralStatsModal}
        onClose={() => setShowReferralStatsModal(false)}
        stats={{
          totalReferrals: referralStats.totalReferrals,
          subscribed: referralStats.subscribed,
          minutesEarned: referralStats.minutesEarned
        }}
        referredUsers={referredUsers}
        formatDate={formatDate}
        zIndex={10000}
      />
      <AmbassadorPaymentsModal
        isOpen={showAmbassadorPaymentsModal}
        onClose={() => setShowAmbassadorPaymentsModal(false)}
        earnings={ambassadorEarnings}
        payouts={ambassadorPayouts}
        zIndex={10000}
      />
    </>
  );
});

UserProfileMenu.displayName = 'UserProfileMenu';

export default UserProfileMenu;